import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Check, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const fmtDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.round((now - d) / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  if (diffMin < 60 * 24) return `il y a ${Math.floor(diffMin / 60)} h`;
  return d.toLocaleDateString('fr-FR');
};

/**
 * NotificationBell — cloche + panneau déroulant des notifications in-app.
 * Poll toutes les 60 s le nombre de non-lus.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get('/notifications/mine');
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch {
      /* silencieux */
    }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
    await load();
  };

  const markAll = async () => {
    try {
      await api.post('/notifications/read-all');
      await load();
    } catch { /* ignore */ }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 rounded-full hover:bg-[#F5EFE1] transition"
        aria-label="Notifications"
        data-testid="notif-bell"
      >
        <Bell className="w-5 h-5 text-[#8F4426]" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[340px] max-w-[90vw] bg-white rounded-lg shadow-2xl border border-[#B85C38]/20 z-40 overflow-hidden" data-testid="notif-panel">
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between bg-[#F5EFE1]/60">
              <b className="text-sm text-[#2B3A2C]">Notifications</b>
              {unread > 0 && (
                <button onClick={markAll} className="text-[11px] text-[#B85C38] hover:underline flex items-center gap-1" data-testid="notif-mark-all">
                  <Check className="w-3 h-3" /> Tout marquer lu
                </button>
              )}
            </div>
            <div className="max-h-[380px] overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-xs text-gray-500">Aucune notification.</div>
              ) : items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => openItem(n)}
                  className={`w-full text-left px-3 py-2 border-b border-gray-50 hover:bg-[#F5EFE1]/40 transition ${!n.read ? 'bg-[#F5EFE1]/25' : ''}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#B85C38] flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className={`text-[13px] ${!n.read ? 'font-bold text-[#2B3A2C]' : 'text-gray-700'}`}>{n.title}</div>
                      <div className="text-[11px] text-gray-600 mt-0.5 line-clamp-2">{n.body}</div>
                      <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                        {fmtDT(n.created_at)}
                        {n.link && <ExternalLink className="w-2.5 h-2.5" />}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationBell;
